import { useTranslation } from '@mukil/i18n';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@jumpstone/ui-react';
import { WAGEPRO_LOCALES } from '../../lib/i18n';
import type { WageProLocale } from '../../lib/i18n';
import { LOCALE_NAME_KEYS, active_wagepro_locale } from './locale_options';

const LOCALE_LABELS: Record<WageProLocale, string> = {
  'en-001': 'EN',
  'si-LK': 'සිං',
  'ta-LK': 'தமி',
};

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * Single-widget language switcher for the employee self-service top bar.
 *
 * The admin `AccountMenu` embeds the always-three-buttons-visible
 * `LocaleSwitcher` inside its own dropdown (with `stopPropagation` handlers
 * to stop item clicks from closing that outer menu) — nesting a second,
 * independent Radix `DropdownMenu` inside that content would fight the outer
 * menu's own portal/focus-scope. This is a separate, purpose-built widget for
 * the employee top bar instead of a variant of the shared component: one
 * compact trigger showing the active locale, expanding to the other two on
 * demand, so the always-visible row shrinks from three buttons to one.
 */
export function EmployeeLocaleSwitcher(): React.JSX.Element {
  const { t, locale, setLocale } = useTranslation('wp');
  // `locale` from useTranslation is the framework's generic `Locale = string`;
  // narrow it the same way apps/wagepro-web/src/main.tsx does at its
  // i18n-provider boundary. Falls back to 'en-001' if the stored/detected
  // locale is ever outside WagePro's supported set.
  const active = active_wagepro_locale(locale);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-11 sm:h-9 px-2.5 gap-1"
          aria-label={t('locale.switcher')}
        >
          <span aria-hidden="true">{LOCALE_LABELS[active]}</span>
          <ChevronDownIcon className="text-[color:var(--ink-muted)]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={active}
          onValueChange={(value) => {
            setLocale(value);
          }}
        >
          {WAGEPRO_LOCALES.map((candidate) => (
            <DropdownMenuRadioItem key={candidate} value={candidate}>
              {t(LOCALE_NAME_KEYS[candidate])}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
